import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiTarget, FiMapPin, FiStar, FiInstagram } = FiIcons;

export default function OnyxCaseStudy() {
  const [activeFaq, setActiveFaq] = useState(null);

  // Brand Images
  const onyxLogo = "https://media-manager-c.questera.ai/greta-media/4320f8aaa9ed428bff142ca67321b3bfce2c39771503d4270b0b625b52f5e6960d1f85712829807a2ee1ac58fcf9446b/images/aW1hZ2UvcG5n/7c8c8b85751d4e3abe8eb9afe75e7baa.png";
  const perryImage = "https://media-manager-c.questera.ai/greta-media/4320f8aaa9ed428bff142ca67321b3bfce2c39771503d4270b0b625b52f5e6960d1f85712829807a2ee1ac58fcf9446b/images/aW1hZ2UvcG5n/c724ae193239e8cd8dadf828fd04befa.png";
  const geeHero = "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&q=80&w=800";
  const geeWalk = "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?auto=format&fit=crop&q=80&w=800";

  const faqs = [
    {
      q: "What kind of businesses do you work with?",
      a: "I work with dog walkers and pet sitters. Not groomers, not trainers, not boarders, not product-based businesses. Just the walker-sitter crossover because that is where Otso Co is built to deliver."
    },
    {
      q: "Are these results typical?",
      a: "These are Gee's real numbers. Every dog walker's starting point is different. What I can promise is the same method, the same honesty, and the same diagnostic quality that got Gee here."
    },
    {
      q: "Did you run any paid ads for Gee in the first 90 days?",
      a: "No. Zero ad spend in the Onyx engagement. Everything in the results section came from organic, operational, and brand work. The audit you would get for $97 covers whether paid ads are the right move for your business or not."
    },
    {
      q: "Who delivers the audit? You or someone else?",
      a: "Me. I do not outsource audits. You pay $97, I personally look at your funnel and record the walkthrough."
    },
    {
      q: "How much is the audit?",
      a: "$97 one time. No subscription, no upsell, no follow-up sales call. You pay once and get a full funnel diagnostic delivered in 48 hours."
    },
    {
      q: "What does the audit actually cover?",
      a: "Your website and booking flow, your Google Business Profile, your social media presence, and your lead tracking setup. I pull up your real data, walk through what is working and what is not, and leave you with one thing to fix this week and 3 things to fix this quarter."
    },
    {
      q: "What if I am not ready for a paid audit yet?",
      a: "Start free. otsoco.com/links has 5 tools built for dog walkers including a content planner, a funnel score, a pricing calculator, an ad budget calculator, and a free estimate. Use those first. Come back when you are ready."
    },
    {
      q: "What happens after I pay for the audit?",
      a: "You get a short intake form with 5 questions. You send me logins to your Google Business Profile, Google Analytics if you have it, and your social channels. Within 48 hours I send back a video walkthrough and a written action plan. That is it."
    }
  ];

  return (
    <>
      <SEO 
        title="Onyx Pet Care Case Study // Otso Co" 
        description="How Gee Centeno went from 4 Google reviews to a real business in 90 days without running ads." 
        path="/onyx-case-study" 
      />

      <div className="min-h-screen bg-brand-cream font-sans text-brand-primaryDark overflow-x-hidden">
        
        {/* SECTION 1: HERO */}
        <section className="bg-brand-primaryDark py-20 md:py-32 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20">
            <div className="w-full md:w-1/2 text-brand-cream text-center md:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
                From 4 Google reviews to a real business in 90 days
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-12 leading-relaxed">
                The Onyx Pet Care story. How one dog walker in Laurel, Maryland rebuilt her social media, her Google presence, and her review flow without running a single ad.
              </p>
              <Link 
                to="/audit" 
                className="inline-block bg-brand-salmon text-brand-primaryDark px-10 py-5 rounded-full font-bold text-xl transition-all hover:bg-white"
              >
                Get your $97 audit
              </Link>
            </div>
            <div className="w-full md:w-1/2">
              <img 
                src={geeHero} 
                alt="Gee Centeno with a dog" 
                className="w-full h-auto rounded-[40px] shadow-2xl object-cover aspect-[4/5] md:aspect-auto" 
              />
            </div>
          </div>
        </section>

        {/* SECTION 2: THE CLIENT */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
            <div className="w-full md:w-1/2 text-lg leading-relaxed space-y-6">
              <h2 className="text-3xl font-bold mb-8">Meet Gee</h2>
              <p>
                Gee Centeno runs Onyx Pet Care in Laurel, Maryland. Dog walker, pet sitter, 5+ years in business.
              </p>
              <p>
                When she came to me she was doing the work every dog walker in her town was doing. Posting on Instagram when she had time. Asking clients for Google reviews when she remembered. Updating her website once a year. Hoping the phone would ring.
              </p>
              <p>
                It was not ringing enough. She had capacity on her schedule and a payroll to cover. Her marketing was a patchwork of things she half-started and never finished.
              </p>
              <p className="font-bold">
                She did not need more hustle. She needed a working engine.
              </p>
            </div>
            <div className="w-full md:w-1/2">
              <img 
                src={geeWalk} 
                alt="Gee on a dog walk" 
                className="w-full h-auto rounded-[40px] shadow-lg object-cover" 
              />
            </div>
          </div>
        </section>

        {/* SECTION 3: THE PROBLEM */}
        <section className="bg-brand-blush py-24 px-6 flex justify-center text-center">
          <div className="max-w-[720px] space-y-8">
            <h2 className="text-3xl font-bold">What was actually broken</h2>
            <div className="text-lg md:text-xl leading-relaxed space-y-6 text-brand-primaryDark">
              <p>Four things, all common. All fixable.</p>
              <p>
                Her Google Business Profile was stale and missed the keywords people in Laurel were typing. 4 reviews. Not enough to outrank competitors.
              </p>
              <p>
                Her website got 230% less traffic than it should have because nothing on it told Google what she did or who she served.
              </p>
              <p>
                Her social media was on-brand for a different business she used to run. Future clients landed on her Instagram and could not tell what year it was, let alone whether they were in the right place.
              </p>
              <p>
                She had no system for asking clients for reviews. She knew she was supposed to. She never got around to it.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 4: THE METHOD */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-3xl font-bold text-brand-primaryDark">How we fixed it in 90 days</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                No paid ads. No website rebuild. No fancy tools. Just the four things every dog walker can fix when she actually commits to fixing them.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: FiTarget,
                  t: "Funnel audit",
                  b: "A full walk-through of her website, booking flow, Google profile, and social touchpoints. Found every place a future client dropped off. Fixed the leaks before adding anything new."
                },
                {
                  icon: FiMapPin,
                  t: "Local visibility",
                  b: "Rebuilt her Google Business Profile from scratch. Optimized categories, photos, service areas, and keywords that actual Laurel residents type. Added weekly GBP posts to hold the ranking."
                },
                {
                  icon: FiStar,
                  t: "Review engine",
                  b: "Designed a simple review request system she could run without me. Tested it on real clients. Reviews climbed from 4 to 15 in under 90 days."
                },
                {
                  icon: FiInstagram,
                  t: "Content and brand",
                  b: "Rebuilt her Instagram and Facebook presence so both feed and profile told the same story her website told. Consistent brand, consistent message, consistent post cadence."
                }
              ].map((card, i) => (
                <div key={i} className="bg-white p-10 rounded-[32px] border-2 border-brand-purple/20 flex flex-col items-start hover:border-brand-purple transition-all shadow-sm">
                  <div className="text-brand-purple mb-6">
                    <SafeIcon icon={card.icon} size={40} />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{card.t}</h3>
                  <p className="text-gray-600 leading-relaxed">{card.b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5: THE RESULTS */}
        <section className="bg-brand-primaryDark py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-16 text-brand-cream text-center">What changed in 90 days</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
              {[
                { n: "15", l: "Google reviews", c: "Up from 4 at the start" },
                { n: "230%", l: "growth in website views in 90 days", c: "Measured in Google Analytics" },
                { n: "100%", l: "lift in Google visibility", c: "Laurel MD local search" },
                { n: "100%", l: "lift in Google visibility", c: "Across all core keywords" },
                { n: "4", l: "Channels rebuilt", c: "Website, Google, Instagram, Facebook" },
                { n: "0", l: "Ad spend used", c: "Every result above came from organic and operational work" }
              ].map((stat, i) => (
                <div key={i} className="bg-white/5 p-8 rounded-3xl border border-white/10 text-center">
                  <p className="text-4xl md:text-5xl font-bold mb-3 text-brand-salmon">{stat.n}</p>
                  <p className="text-brand-cream font-bold text-sm uppercase tracking-widest mb-2">{stat.l}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold">{stat.c}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6: ABOUT PERRY */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
            <div className="w-full md:w-5/12">
              <img 
                src={perryImage} 
                alt="Perry Dasilva" 
                className="w-full h-auto rounded-[40px] shadow-xl border-4 border-white" 
              />
            </div>
            <div className="w-full md:w-7/12 space-y-6 text-lg leading-relaxed">
              <h2 className="text-3xl font-bold">Why I took this on</h2>
              <p>
                I am a social media buyer at a camps and endurance events company in my day job. I manage over $10,000 a month in Meta and Google ad spend across 5 brand accounts. That is where my real paid media reps come from.
              </p>
              <p>
                Most marketing people chase the loud niches. Ecommerce. SaaS. Real estate. Pet care gets ignored because it does not sell a glamorous story.
              </p>
              <p>
                I picked it on purpose.
              </p>
              <p>
                My partner and I have a dog. Every dog walker I have ever met works harder than anyone I know for less credit. The industry deserves better marketing than it is getting. Otso Co exists because I wanted to build the one thing that was missing. A marketing brand that actually understands how a dog walker makes money, where she loses clients, and what moves the needle.
              </p>
              <p>
                Gee was the first business I took on. She will not be the last.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIAL */}
        <section className="bg-brand-purple py-24 px-6 text-center text-brand-cream">
          <div className="max-w-4xl mx-auto space-y-10">
            <p className="text-2xl md:text-4xl font-bold leading-tight italic">
              "Before Perry I was the marketing person. I hated it and I was not good at it. Three months in and I stopped dreading opening my phone. The leads started coming in and I could actually answer them."
            </p>
            <div className="space-y-1">
              <p className="text-xl font-bold uppercase tracking-widest text-brand-blush">Gee Centeno</p>
              <p className="text-white/60 font-bold">Owner, Onyx Pet Care · Laurel, Maryland</p>
            </div>
          </div>
        </section>

        {/* SECTION 8: THE OFFER */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-[40px] border-2 border-brand-purple p-10 md:p-16 text-center shadow-xl">
              <h2 className="text-3xl font-bold mb-8">Ready to see where your business stands</h2>
              <div className="text-lg text-gray-700 leading-relaxed mb-12 space-y-6">
                <p>
                  Gee did not guess her way to these numbers. She started with a real audit of her website, her Google profile, her social presence, and her lead flow. That audit is how we found what was broken before we fixed anything.
                </p>
                <p>
                  The same audit is available to you for $97. I review your funnel end to end, pull up the same data I pulled for Gee, and send you back a video walkthrough with the one thing to fix this week and the 3 things to fix this quarter.
                </p>
                <p className="font-bold text-brand-primaryDark">
                  Delivered in 48 hours. No meetings. No upsell call.
                </p>
                <p>
                  If you are serious about building what Gee built, start where she started.
                </p>
              </div>
              
              <div className="bg-brand-cream/50 rounded-2xl p-8 mb-10 border border-gray-100">
                <p className="text-3xl font-bold text-brand-primaryDark mb-2">$97 one time</p>
                <p className="text-sm text-gray-500 max-w-sm mx-auto font-medium">
                  A full diagnostic of your funnel, delivered as a video walkthrough and written action plan in 48 hours.
                </p>
              </div>

              <Link 
                to="/audit" 
                className="inline-block w-full sm:w-auto bg-brand-salmon text-brand-primaryDark px-12 py-5 rounded-full font-bold text-xl transition-all hover:bg-brand-primaryDark hover:text-white shadow-lg"
              >
                Get your $97 audit
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 9: FAQ */}
        <section className="bg-brand-cream py-24 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Common questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                  <button 
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    className="w-full flex justify-between items-center p-6 text-left font-bold text-brand-primaryDark hover:text-brand-purple transition-colors"
                  >
                    {faq.q}
                    <SafeIcon icon={FiPlus} className={`transition-transform duration-200 ${activeFaq === i ? 'rotate-45' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {activeFaq === i && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }} 
                        animate={{ height: 'auto', opacity: 1 }} 
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <div className="p-6 pt-0 text-gray-600 border-t border-gray-50 leading-relaxed">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 10: FINAL CTA */}
        <section className="bg-brand-primaryDark py-20 px-6 text-center text-brand-cream">
          <div className="max-w-2xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">Start where Gee started</h2>
            <p className="text-xl text-white/70 leading-relaxed">
              One audit. $97. Delivered in 48 hours. No meetings, no upsell, no retainer pitch at the end.
            </p>
            <Link 
              to="/audit" 
              className="inline-block bg-brand-salmon text-brand-primaryDark px-12 py-5 rounded-full font-bold text-xl transition-all hover:bg-white shadow-2xl"
            >
              Get your $97 audit
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}